(() => {
  const TRANSITION_MS = 650;

  const initHeroBanner = (root) => {
    if (!root || root.dataset.souleveHeroReady === 'true') return;

    const slides = Array.from(root.querySelectorAll('[data-souleve-hero-slide]'));
    if (slides.length < 2) return;

    root.dataset.souleveHeroReady = 'true';

    const dots = Array.from(root.querySelectorAll('[data-souleve-hero-dot]'));
    const prevButton = root.querySelector('[data-souleve-hero-prev]');
    const nextButton = root.querySelector('[data-souleve-hero-next]');
    const autoplay = root.dataset.autoplay === 'true';
    const autoplaySpeed = Math.max(4, Number(root.dataset.autoplaySpeed) || 6) * 1000;
    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const duration = reduceMotion ? 0 : TRANSITION_MS;

    let index = slides.findIndex((slide) => slide.classList.contains('is-active'));
    if (index < 0) index = 0;
    let timer = null;
    let animating = false;
    let inView = true;
    let pausedByInteraction = false;

    const pauseInteraction = () => {
      pausedByInteraction = true;
      stop();
      setDotProgress(index, false);
    };

    const resumeInteraction = () => {
      pausedByInteraction = false;
      schedule();
    };

    const isPointerOverRoot = () => root.matches(':hover') || root.contains(document.activeElement);

    const setDotProgress = (activeIndex, running) => {
      dots.forEach((dot, i) => {
        const progress = dot.querySelector('.souleve-hero-banner__dot-progress');
        const active = i === activeIndex;
        dot.classList.toggle('is-active', active);
        dot.setAttribute('aria-selected', active ? 'true' : 'false');
        if (!progress) return;
        progress.style.transitionDuration = '0ms';
        progress.style.transform = 'scaleX(0)';
        if (active && running && !reduceMotion) {
          requestAnimationFrame(() => {
            progress.style.transitionDuration = `${autoplaySpeed}ms`;
            progress.style.transform = 'scaleX(1)';
          });
        } else if (active) {
          progress.style.transform = 'scaleX(1)';
        }
      });
    };

    const resetSlideState = (slide) => {
      slide.classList.remove('is-active', 'is-leaving', 'is-entering');
      slide.setAttribute('aria-hidden', 'true');
    };

    const setActive = (nextIndex, { restartProgress = true } = {}) => {
      if (animating) return;
      const target = (nextIndex + slides.length) % slides.length;
      if (target === index) {
        if (restartProgress) setDotProgress(index, autoplay && inView && !pausedByInteraction);
        return;
      }

      if (index === slides.length - 1 && target === 0) {
        root.dataset.direction = 'next';
      } else if (index === 0 && target === slides.length - 1) {
        root.dataset.direction = 'prev';
      } else {
        root.dataset.direction = target > index ? 'next' : 'prev';
      }

      const current = slides[index];
      const next = slides[target];

      animating = true;
      root.classList.add('is-animating');

      current.classList.remove('is-active');
      current.classList.add('is-leaving');
      current.setAttribute('aria-hidden', 'true');

      next.classList.add('is-entering');
      next.setAttribute('aria-hidden', 'false');

      requestAnimationFrame(() => {
        next.classList.add('is-active');
      });

      index = target;
      setDotProgress(index, restartProgress && autoplay && inView && !pausedByInteraction);

      window.setTimeout(() => {
        resetSlideState(current);
        next.classList.remove('is-entering');
        next.classList.add('is-active');
        root.classList.remove('is-animating');
        animating = false;
      }, duration);
    };

    const stop = () => {
      if (timer) {
        clearTimeout(timer);
        timer = null;
      }
      dots.forEach((dot) => {
        const progress = dot.querySelector('.souleve-hero-banner__dot-progress');
        if (progress) {
          progress.style.transitionDuration = '0ms';
        }
      });
    };

    const schedule = () => {
      stop();
      if (!autoplay || !inView || reduceMotion || pausedByInteraction) {
        setDotProgress(index, false);
        return;
      }
      setDotProgress(index, true);
      timer = window.setTimeout(() => {
        setActive(index + 1);
        schedule();
      }, autoplaySpeed);
    };

    const go = (nextIndex) => {
      pauseInteraction();
      setActive(nextIndex, { restartProgress: true });
      if (!isPointerOverRoot()) {
        resumeInteraction();
      }
    };

    prevButton?.addEventListener('click', () => go(index - 1));
    nextButton?.addEventListener('click', () => go(index + 1));
    dots.forEach((dot) => {
      dot.addEventListener('click', () => go(Number(dot.dataset.index) || 0));
    });

    let touchStartX = 0;
    let touchStartY = 0;
    let touchDeltaX = 0;
    let touchTracking = false;

    root.addEventListener(
      'touchstart',
      (event) => {
        if (event.touches.length !== 1 || animating) return;
        touchTracking = true;
        touchStartX = event.touches[0].clientX;
        touchStartY = event.touches[0].clientY;
        touchDeltaX = 0;
        pauseInteraction();
      },
      { passive: true }
    );

    root.addEventListener(
      'touchmove',
      (event) => {
        if (!touchTracking || event.touches.length !== 1) return;
        const deltaX = event.touches[0].clientX - touchStartX;
        const deltaY = event.touches[0].clientY - touchStartY;
        if (Math.abs(deltaY) > Math.abs(deltaX) && Math.abs(deltaY) > 12) {
          touchTracking = false;
          return;
        }
        touchDeltaX = deltaX;
      },
      { passive: true }
    );

    root.addEventListener(
      'touchend',
      () => {
        if (!touchTracking) {
          window.setTimeout(() => {
            if (!isPointerOverRoot()) resumeInteraction();
          }, 250);
          return;
        }

        const swipeThreshold = 48;
        if (Math.abs(touchDeltaX) >= swipeThreshold) {
          go(touchDeltaX < 0 ? index + 1 : index - 1);
        } else if (!isPointerOverRoot()) {
          resumeInteraction();
        }

        touchTracking = false;
        touchDeltaX = 0;
      },
      { passive: true }
    );

    root.addEventListener(
      'touchcancel',
      () => {
        touchTracking = false;
        touchDeltaX = 0;
        if (!isPointerOverRoot()) resumeInteraction();
      },
      { passive: true }
    );

    root.addEventListener('pointerenter', pauseInteraction);
    root.addEventListener('pointerleave', resumeInteraction);
    root.addEventListener('pointerdown', pauseInteraction);
    root.addEventListener('focusin', pauseInteraction);
    root.addEventListener('focusout', (event) => {
      if (!root.contains(event.relatedTarget) && !isPointerOverRoot()) {
        resumeInteraction();
      }
    });

    document.addEventListener('visibilitychange', () => {
      if (document.hidden) stop();
      else schedule();
    });

    if ('IntersectionObserver' in window) {
      const observer = new IntersectionObserver(
        ([entry]) => {
          inView = entry.isIntersecting;
          if (inView) schedule();
          else stop();
        },
        { threshold: 0.35 }
      );
      observer.observe(root);
    }

    slides.forEach((slide, i) => {
      resetSlideState(slide);
      if (i === index) {
        slide.classList.add('is-active');
        slide.setAttribute('aria-hidden', 'false');
      }
    });

    requestAnimationFrame(() => {
      root.classList.add('is-ready');
      setDotProgress(index, false);
      schedule();
    });
  };

  const boot = () => {
    document.querySelectorAll('[data-souleve-hero-banner]').forEach(initHeroBanner);
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

  document.addEventListener('shopify:section:load', (event) => {
    const banner =
      event.target.querySelector?.('[data-souleve-hero-banner]') ||
      event.target.closest?.('[data-souleve-hero-banner]');
    if (banner) {
      banner.dataset.souleveHeroReady = 'false';
      banner.classList.remove('is-ready', 'is-animating');
      initHeroBanner(banner);
    }
  });
})();
